import { type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  PARTNER_ACCOUNT_EDIT_REASONS,
  PARTNER_ACCOUNT_REACTIVATE_REASONS,
  PARTNER_ACCOUNT_SUSPEND_REASONS,
  PARTNER_LIMIT_REASONS,
  PARTNER_LIMIT_REJECT_REASONS,
  PARTNER_MEMBER_REINSTATE_REASONS,
  PARTNER_MEMBER_REVOKE_REASONS,
  PARTNER_MEMBER_ROLES,
  PARTNER_MEMBER_SUSPEND_REASONS,
  type ApiError,
  type PartnerAccountDetailView,
  type PartnerAccountMoney,
  type PartnerBillingAccount,
  type PartnerMemberRole,
  type PartnerMemberView,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Drawer, EmptyState, Field, MoneyInput, Notice, Panel, Select, Table, TextInput, FormRow, DrawerHeader, DrawerBody, Text, Row, Stack, SectionHead, PanelBody } from '@vello/web-theme';
import { formatMarketInstant, marketDisplayName, limitDecision, maySelfApprove, parseCoordinate, partnerAccountActions } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { PartnerCostControlPanel } from './partner-cost-control-panel';
import { LoadingBlock } from './loading-block';
import { DrawerStack } from './drawer-stack';
import { ScopeMarketNotice, useScopeMarket } from './scope-market';

type Explain = (e: ApiError) => string;

export function PartnerAccountsPanel(): ReactElement {
  const { runtime, grants, markets, me } = useAdmin();
  const { t, lang } = useT();
  const { v, fail, explain: said } = useVocab();
  const { toast } = useConsole();
  const actions = partnerAccountActions(grants);
  const [accounts, setAccounts] = useState<readonly PartnerBillingAccount[] | undefined>(undefined);
  const [listRefused, setListRefused] = useState(false);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [registering, setRegistering] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const explain: Explain = useCallback((e) => said(e) ?? t('pob.refused', { code: fail(e) }), [t, said, fail]);

  useEffect(() => {
    let cancelled = false;
    void runtime.partnerBilling.accounts().then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setAccounts(res.value.accounts);
        setListRefused(false);
      } else {
        setAccounts([]);
        setListRefused(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, refresh]);

  return (
    <Panel padded={false}>
      <SectionHead
        title={t('pob.partners.title')}
        subtitle={t('pob.partners.subtitle')}
        actions={actions.mayManage ? <Button variant="secondary" onClick={() => setRegistering(true)}>{t('pob.partners.new')}</Button> : undefined}
      />
      {listRefused && (
        <PanelBody>
          <Notice tone="neutral">{t('pob.partners.listUnavailable')}</Notice>
        </PanelBody>
      )}
      <Table
        columns={[
          { key: 'name', header: t('pob.col.partner'), render: (a: PartnerBillingAccount) => a.displayName ?? a.legalName },
          { key: 'markets', header: t('pob.col.markets'), render: (a: PartnerBillingAccount) => <span className="tnum">{a.operatingMarkets.map((id) => marketDisplayName(id, markets)).join(', ')}</span> },
          { key: 'status', header: t('pob.col.status'), render: (a: PartnerBillingAccount) => <Badge tone={a.status === 'ACTIVE' ? 'success' : 'warning'}>{v('business.account_status', a.status)}</Badge> },
        ]}
        rows={accounts ?? []}
        rowKey={(a) => a.accountId}
        {...(actions.mayView ? { onRowClick: (a: PartnerBillingAccount) => setOpenId(a.accountId) } : {})}
        loading={accounts === undefined}
        loadingLabel={t('ui.loading')}
        empty={<EmptyState title={t('pob.partners.empty')} />}
      />
      <RegisterPartnerDrawer
        open={registering}
        onClose={() => setRegistering(false)}
        explain={explain}
        onRegistered={(id) => {
          setRegistering(false);
          toast({ tone: 'success', message: t('pob.register.done') });
          setRefresh((n) => n + 1);
          setOpenId(id);
        }}
      />
      <PartnerAccountDrawer accountId={openId} onClose={() => setOpenId(undefined)} explain={explain} meId={me?.id} actions={actions} lang={lang} markets={markets.map((m) => ({ id: m.id, countryCode: m.countryCode, name: m.name }))} onChanged={() => setRefresh((n) => n + 1)} />
    </Panel>
  );
}

function RegisterPartnerDrawer({ open, onClose, onRegistered, explain }: { open: boolean; onClose: () => void; onRegistered: (accountId: string) => void; explain: Explain }): ReactElement {
  const { runtime, markets } = useAdmin();
  const { market: scopeMarket, choices: marketChoices } = useScopeMarket();
  const { t, lang } = useT();
  const { v } = useVocab();
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [door, setDoor] = useState({ address: '', latitude: '', longitude: '' });
  const [timezone, setTimezone] = useState('');
  const [currency, setCurrency] = useState('');
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // The partner's operating city is the header scope's (owner, 2026-10-12): one selector, at the top of the page.
  const market = markets.find((m) => m.id === scopeMarket);
  const doorTyped = door.address.trim() !== '' || door.latitude.trim() !== '' || door.longitude.trim() !== '';
  const lat = parseCoordinate(door.latitude, 'latitude');
  const lng = parseCoordinate(door.longitude, 'longitude');
  const missing = [
    ...(legalName.trim() === '' ? [t('pob.field.legalName')] : []),
    ...(doorTyped && (lat === undefined || lng === undefined || door.address.trim() === '') ? [t('pob.field.door')] : []),
    ...(!isIso4217Currency(currency) || amount === undefined ? [t('pob.field.limit')] : []),
  ];

  const submit = async (): Promise<void> => {
    if (market === undefined || amount === undefined) return;
    setBusy(true);
    setError(undefined);
    const res = await runtime.partnerAccounts.register({
      legalName: legalName.trim(),
      ...(displayName.trim() !== '' ? { displayName: displayName.trim() } : {}),
      countryCode: market.countryCode,
      ...(timezone.trim() !== '' ? { billingTimezone: timezone.trim() } : {}),
      markets: [{ marketId: market.id, ...(doorTyped && lat !== undefined && lng !== undefined ? { pickup: { latitude: lat, longitude: lng, address: door.address.trim() } } : {}) }],
      spendLimit: { amountMinor: amount.toString(), currency },
    });
    setBusy(false);
    if (res.ok) onRegistered(res.value.account.accountId);
    else setError(explain(res.error));
  };

  return (
    <Drawer open={open} onClose={onClose} labelledBy="pob-register-title">
      <DrawerHeader titleId="pob-register-title" title={t('pob.partners.new')} subtitle={t('pob.register.hint')} />
      <DrawerBody>
        {market === undefined ? <ScopeMarketNotice choices={marketChoices} /> : (
        <DrawerStack>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
          <Field label={t('pob.field.legalName')} htmlFor="pob-legal"><TextInput id="pob-legal" value={legalName} onChange={(e) => setLegalName(e.target.value)} /></Field>
          <Field label={t('pob.field.displayName')} htmlFor="pob-display"><TextInput id="pob-display" value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></Field>
          <Field label={t('pob.field.timezone')} htmlFor="pob-tz" hint={t('pob.field.timezone.hint')}><TextInput id="pob-tz" value={timezone} placeholder={market?.timezone ?? ''} onChange={(e) => setTimezone(e.target.value)} /></Field>
          <Field label={t('pob.field.door')} htmlFor="pob-door"><TextInput id="pob-door" value={door.address} onChange={(e) => setDoor({ ...door, address: e.target.value })} /></Field>
          <FormRow>
            <Field label={t('pob.field.latitude')} htmlFor="pob-lat"><TextInput id="pob-lat" inputMode="decimal" className="tnum" value={door.latitude} onChange={(e) => setDoor({ ...door, latitude: e.target.value })} /></Field>
            <Field label={t('pob.field.longitude')} htmlFor="pob-lng"><TextInput id="pob-lng" inputMode="decimal" className="tnum" value={door.longitude} onChange={(e) => setDoor({ ...door, longitude: e.target.value })} /></Field>
          </FormRow>
          <Field label={t('pob.field.currency')} htmlFor="pob-currency"><TextInput id="pob-currency" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} /></Field>
          {isIso4217Currency(currency) && (
            <MoneyInput id="pob-limit" label={t('pob.field.limit')} currency={currency} locale={lang} valueMinor={amount} onChangeMinor={setAmount} issueText={(issue) => v('format.money_input_issue', issue)} required />
          )}
          {missing.length > 0 && <Text as="p" role="caption" tone="secondary">{t('pob.invalid', { what: missing.join(', ') })}</Text>}
          <div>
            <Button variant="primary" disabled={busy || missing.length > 0} onClick={() => void submit()}>{t('pob.register.submit')}</Button>
          </div>
        </DrawerStack>
        )}
      </DrawerBody>
    </Drawer>
  );
}

function PartnerAccountDrawer({
  accountId,
  onClose,
  explain,
  meId,
  actions,
  lang,
  markets,
  onChanged,
}: {
  accountId: string | undefined;
  onClose: () => void;
  explain: Explain;
  meId: string | undefined;
  actions: ReturnType<typeof partnerAccountActions>;
  lang: string;
  markets: readonly { readonly id: string; readonly countryCode: string; readonly name: string }[];
  onChanged: () => void;
}): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t } = useT();
  const { v, has } = useVocab();
  const countryName = useCountryName();
  const { confirm, toast } = useConsole();
  const [detail, setDetail] = useState<PartnerAccountDetailView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [editName, setEditName] = useState('');
  const [editTz, setEditTz] = useState('');
  const [addMarket, setAddMarket] = useState('');
  const [limitAmount, setLimitAmount] = useState<bigint | undefined>(undefined);
  const [limitReason, setLimitReason] = useState<string>('CREDIT_INCREASE');
  const [invite, setInvite] = useState({ identifier: '', role: 'agent' as PartnerMemberRole });

  const load = useCallback(async (): Promise<void> => {
    if (accountId === undefined) return;
    const res = await runtime.partnerAccounts.detail(accountId);
    if (res.ok) {
      setDetail(res.value);
      setEditName(res.value.account.displayName ?? '');
      setEditTz(res.value.account.billingTimezone ?? '');
      setError(undefined);
    } else setError(explain(res.error));
  }, [accountId, runtime, explain]);

  useEffect(() => {
    setDetail(undefined);
    void load();
  }, [load]);

  const money = (m: PartnerAccountMoney | null): string =>
    m !== null && /^\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : '—';
  const done = async (res: { ok: boolean; error?: ApiError }, message: string): Promise<void> => {
    if (res.ok) {
      toast({ tone: 'success', message: t(message) });
      await load();
      onChanged();
    } else if (res.error !== undefined) setError(explain(res.error));
  };

  const a = detail?.account;
  // The field ADDS a city: the ones the partner already serves are not options (they are listed above), so the empty choice is "keep the current cities" and a save never writes another city by accident (D4-F-002).
  const addableMarkets = a === undefined ? [] : markets.filter((m) => m.countryCode === a.countryCode && !a.markets.some((x) => x.marketId === m.id));
  const pending = detail?.pendingLimitChange ?? null;
  const decision = pending !== null ? limitDecision(pending, meId, actions, maySelfApprove(grants)) : undefined;
  const reasonPick = async (title: string, body: string, codes: readonly string[], tone: 'primary' | 'danger'): Promise<string | undefined> => {
    const answer = await confirm({ title, body, confirmLabel: title, cancelLabel: t('ui.cancel'), tone, reasons: codes.map((c) => v('business.partner_change_reason', c)), reasonLabel: t('pob.field.reason'), reasonsOnly: true });
    if (!answer.confirmed || answer.reason === undefined) return undefined;
    return codes.find((c) => v('business.partner_change_reason', c) === answer.reason);
  };

  const saveEdits = async (): Promise<void> => {
    if (a === undefined) return;
    const reason = await reasonPick(t('pob.edit.save'), t('pob.section.data'), PARTNER_ACCOUNT_EDIT_REASONS, 'primary');
    if (reason === undefined) return;
    const extra = markets.find((m) => m.id === addMarket);
    const res = await runtime.partnerAccounts.edit(a.accountId, {
      expectedUpdatedAt: a.updatedAt,
      reasonCode: reason,
      displayName: editName.trim() === '' ? null : editName.trim(),
      ...(editTz.trim() !== '' ? { billingTimezone: editTz.trim() } : {}),
      ...(extra !== undefined ? { markets: [...a.markets.map((m) => ({ marketId: m.marketId, ...(m.pickup !== null ? { pickup: m.pickup } : {}) })), { marketId: extra.id }] } : {}),
    });
    setAddMarket('');
    await done(res, 'pob.edit.saved');
  };

  const setStatus = async (to: 'ACTIVE' | 'SUSPENDED'): Promise<void> => {
    if (a === undefined) return;
    const reason = await reasonPick(t(to === 'SUSPENDED' ? 'pob.status.suspend' : 'pob.status.reactivate'), t('pob.status.suspend.body'), to === 'SUSPENDED' ? PARTNER_ACCOUNT_SUSPEND_REASONS : PARTNER_ACCOUNT_REACTIVATE_REASONS, to === 'SUSPENDED' ? 'danger' : 'primary');
    if (reason === undefined) return;
    await done(await runtime.partnerAccounts.setStatus(a.accountId, to, reason), 'pob.status.done');
  };

  const decide = async (approve: boolean): Promise<void> => {
    if (pending === null) return;
    if (approve) {
      await done(await runtime.partnerAccounts.approveLimit(pending.changeId), 'pob.limit.approved');
      return;
    }
    const reason = await reasonPick(t('pob.limit.reject'), t('pob.section.limit'), PARTNER_LIMIT_REJECT_REASONS, 'danger');
    if (reason === undefined) return;
    await done(await runtime.partnerAccounts.rejectLimit(pending.changeId, reason), 'pob.limit.rejected');
  };

  const memberAction = async (m: PartnerMemberView, action: 'suspend' | 'reinstate' | 'revoke'): Promise<void> => {
    const codes = action === 'suspend' ? PARTNER_MEMBER_SUSPEND_REASONS : action === 'reinstate' ? PARTNER_MEMBER_REINSTATE_REASONS : PARTNER_MEMBER_REVOKE_REASONS;
    const reason = await reasonPick(v('partner.member_action', action), action === 'revoke' ? t('pob.member.revoke.body') : (m.identifier ?? m.userId), codes, action === 'reinstate' ? 'primary' : 'danger');
    if (reason === undefined) return;
    await done(await runtime.partnerAccounts.memberAction(m.membershipId, action, reason), 'pob.member.done');
  };

  const currency = a?.spendLimit?.currency;
  return (
    <Drawer open={accountId !== undefined} onClose={onClose} labelledBy="pob-account-title">
      {accountId !== undefined && (
        <>
          <DrawerHeader titleId="pob-account-title" title={a?.displayName ?? a?.legalName ?? '…'}>
          {a !== undefined && (
            <Row gap={2} align="center">
              <Badge tone={a.status === 'ACTIVE' ? 'success' : 'warning'}>{v('business.account_status', a.status)}</Badge>
              {a.statusReasonCode !== null && <Text role="caption" tone="secondary">{v('business.partner_change_reason', a.statusReasonCode)}</Text>}
            </Row>
          )}
          </DrawerHeader>
          <DrawerBody>
            <DrawerStack gap={'var(--space-5)'}>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {a === undefined ? (
                <LoadingBlock />
              ) : (
                <>
                  <Stack as="section" gap={3} aria-labelledby="pob-data">
                    <Text as="h3" role="label" id="pob-data">{t('pob.section.data')}</Text>
                    <Text as="p" role="caption" tone="secondary">{a.legalName} · {countryName(a.countryCode)} · <span className="tnum">{a.markets.map((m) => (markets.find((x) => x.id === m.marketId)?.name ?? m.marketId) + (m.pickup !== null ? ` — ${m.pickup.address}` : '')).join('; ')}</span></Text>
                    {actions.mayManage && (
                      <>
                        <Field label={t('pob.field.displayName')} htmlFor="pob-edit-name"><TextInput id="pob-edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} /></Field>
                        <Field label={t('pob.field.timezone')} htmlFor="pob-edit-tz"><TextInput id="pob-edit-tz" value={editTz} onChange={(e) => setEditTz(e.target.value)} /></Field>
                        {addableMarkets.length > 0 && (
                          <Field label={t('pob.field.addMarket')} htmlFor="pob-edit-market">
                            <Select id="pob-edit-market" value={addMarket} onChange={(e) => setAddMarket(e.target.value)}>
                              <option value="">{t('pob.field.addMarket.none')}</option>
                              {addableMarkets.map((m) => <option key={m.id} value={m.id}>{`${m.name} (${m.id})`}</option>)}
                            </Select>
                          </Field>
                        )}
                        <Row gap={3} align="center" wrap>
                          <Button variant="secondary" onClick={() => void saveEdits()}>{t('pob.edit.save')}</Button>
                          {a.status === 'ACTIVE' ? (
                            <Button variant="danger" onClick={() => void setStatus('SUSPENDED')}>{t('pob.status.suspend')}</Button>
                          ) : (
                            <Button variant="secondary" onClick={() => void setStatus('ACTIVE')}>{t('pob.status.reactivate')}</Button>
                          )}
                        </Row>
                      </>
                    )}
                  </Stack>

                  <Stack as="section" gap={3} aria-labelledby="pob-limit">
                    <Text as="h3" role="label" id="pob-limit">{t('pob.section.limit')}</Text>
                    <p className="tnum">{a.spendLimit !== null ? t('pob.limit.current', { amount: money(a.spendLimit) }) : t('pob.limit.none')}</p>
                    {pending !== null && (
                      <Stack gap={2}>
                        <Notice tone="warning">
                          <span>{t('pob.limit.pending', { from: money(pending.from), to: money(pending.to), reason: v('business.partner_change_reason', pending.reasonCode), who: pending.proposedByLabel ?? t('pob.someone') })}</span>
                        </Notice>
                        {decision?.allowed === true ? (
                          <Row gap={3} align="center" wrap>
                            <Button variant="primary" onClick={() => void decide(true)}>{t('pob.limit.approve')}</Button>
                            <Button variant="secondary" onClick={() => void decide(false)}>{t('pob.limit.reject')}</Button>
                          </Row>
                        ) : decision?.block === 'SELF_APPROVAL' || decision?.block === 'NO_PERMISSION' ? (
                          <Text as="p" role="caption" tone="secondary">{v('partner.limit_decision_block', decision.block)}</Text>
                        ) : null}
                      </Stack>
                    )}
                    {actions.mayPropose && pending === null && currency !== undefined && (
                      <Stack gap={2}>
                        <MoneyInput id="pob-new-limit" label={t('pob.limit.propose')} currency={currency} locale={lang} valueMinor={limitAmount} onChangeMinor={setLimitAmount} issueText={(issue) => v('format.money_input_issue', issue)} />
                        <Field label={t('pob.field.reason')} htmlFor="pob-limit-reason">
                          <Select id="pob-limit-reason" value={limitReason} onChange={(e) => setLimitReason(e.target.value)}>
                            {PARTNER_LIMIT_REASONS.filter((r) => r !== 'INITIAL_LIMIT').map((r) => <option key={r} value={r}>{v('business.partner_change_reason', r)}</option>)}
                          </Select>
                        </Field>
                        <div>
                          <Button variant="secondary" disabled={limitAmount === undefined} onClick={() => void (async () => {
                            if (limitAmount === undefined) return;
                            const res = await runtime.partnerAccounts.proposeLimit(a.accountId, { amountMinor: limitAmount.toString(), currency, reasonCode: limitReason });
                            setLimitAmount(undefined);
                            await done(res, 'pob.limit.proposed');
                          })()}>{t('pob.limit.propose')}</Button>
                        </div>
                      </Stack>
                    )}
                  </Stack>

                  <Stack as="section" gap={3} aria-labelledby="pob-members">
                    <Text as="h3" role="label" id="pob-members">{t('pob.section.members')}</Text>
                    <Table
                      columns={[
                        { key: 'who', header: t('pob.member.identifier'), render: (m: PartnerMemberView) => m.identifier ?? m.userId },
                        { key: 'role', header: t('pob.member.role'), render: (m: PartnerMemberView) => v('business.membership_role', m.role) },
                        { key: 'status', header: t('pob.col.status'), render: (m: PartnerMemberView) => <Badge tone={m.status === 'ACTIVE' ? 'success' : m.status === 'SUSPENDED' ? 'warning' : 'neutral'}>{v('business.membership_status', m.status)}</Badge> },
                        {
                          key: 'act',
                          header: '',
                          render: (m: PartnerMemberView) =>
                            !actions.mayManage || m.status === 'REVOKED' ? null : (
                              <Row gap={2} align="stretch" justify="end">
                                {m.status === 'ACTIVE' ? (
                                  <Button variant="ghost" onClick={() => void memberAction(m, 'suspend')}>{v('partner.member_action', 'suspend')}</Button>
                                ) : (
                                  <Button variant="ghost" onClick={() => void memberAction(m, 'reinstate')}>{v('partner.member_action', 'reinstate')}</Button>
                                )}
                                <Button variant="ghost" onClick={() => void memberAction(m, 'revoke')}>{v('partner.member_action', 'revoke')}</Button>
                              </Row>
                            ),
                        },
                      ]}
                      rows={detail?.members ?? []}
                      rowKey={(m) => m.membershipId}
                      empty={<Text as="p" role="caption" tone="secondary" className="vello-admin-a-pad">{t('pob.member.empty')}</Text>}
                    />
                    {actions.mayManage && (
                      <FormRow
                        action={
                          <>
                            <Button variant="secondary" disabled={invite.identifier.trim() === ''} onClick={() => void (async () => {
                              const res = await runtime.partnerAccounts.inviteMember(a.accountId, invite.identifier.trim(), invite.role);
                              if (res.ok) setInvite({ identifier: '', role: 'agent' });
                              await done(res, 'pob.member.invited');
                            })()}>{t('pob.member.invite')}</Button>
                          </>
                        }
                      >
                        <Field label={t('pob.member.identifier')} htmlFor="pob-invite-id" hint={t('pob.member.identifier.hint')}><TextInput id="pob-invite-id" type="email" value={invite.identifier} onChange={(e) => setInvite({ ...invite, identifier: e.target.value })} /></Field>
                        <Field label={t('pob.member.role')} htmlFor="pob-invite-role">
                          <Select id="pob-invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as PartnerMemberRole })}>
                            {PARTNER_MEMBER_ROLES.map((r) => <option key={r} value={r}>{v('business.membership_role', r)}</option>)}
                          </Select>
                        </Field>
                      </FormRow>
                    )}
                  </Stack>

                  <Stack as="section" gap={3} aria-labelledby="pcc-section">
                    <Text as="h3" role="label" id="pcc-section">{t('pcc.section')}</Text>
                    {/* D479: cost centers, monthly limits, billing model, category scope and quick-order zones of the account. */}
                    <PartnerCostControlPanel account={a} mayManage={actions.mayManage} mayProposeFare={actions.mayProposeFare} mayApproveFare={actions.mayApproveFare} meId={meId} explain={explain} />
                  </Stack>

                  <Stack as="section" gap={3} aria-labelledby="pob-history">
                    <Text as="h3" role="label" id="pob-history">{t('pob.section.history')}</Text>
                    {(detail?.events ?? []).length === 0 ? (
                      <Text as="p" role="caption" tone="secondary">{t('pob.history.empty')}</Text>
                    ) : (
                      (detail?.events ?? []).slice(0, 20).map((e) => (
                        <Text as="div" role="caption" tone="secondary" key={e.eventId}>
                          <span className="tnum">{formatMarketInstant(e.at, a.billingTimezone ?? 'UTC', lang)}</span> — {t('pob.history.row', { kind: v('partner.account_event_kind', e.kind), reason: has('business.partner_change_reason', e.reasonCode) ? v('business.partner_change_reason', e.reasonCode) : v('partner.account_event_system_reason', e.reasonCode), who: e.actorLabel ?? t('pob.someone') })}
                        </Text>
                      ))
                    )}
                  </Stack>
                </>
              )}
            </DrawerStack>
          </DrawerBody>
        </>
      )}
    </Drawer>
  );
}
